import { Card, CardBody } from '@nextui-org/react';
import { checkType, CtyButton, CtyShowToast, CtySpinner, CustomTabs, extractCommandJsonFromAttrs, formatKeyForDisplay, JsonMapperMonacoEditor, NavSidebar, parseFieldDefs } from '@circuitry-ai/doc-ui';
import {
    getWebAgentStartUrl,
    parseInstructionsGroups,
    renumberWebAgentSteps,
    useGetAiWorkerConfigurationsQuery,
    useTypedSelector,
    useUpdateAiWorkerConfigurationMutation,
    useEntityResourcePermissions,
    type WebAgentStep,
    type WebAgentCustomInstruction,
} from '@circuitry-ai/doc-data';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { deepClone, toObject } from './agentutils';
import { StepRow } from './WebAgentStepRow';
import { CustomInstructionRow } from './WebAgentCustomRow';
import { WebAgentInstructionsAssistant, type AssistantApplyPayload } from './WebAgentInstructionsAssistant';

type Groups = Record<string, any[]>;

export interface WebAgentInstructionsProps {
    agent?: any;
    configId?: string;
    configVersion?: number | string;
    configVersionId?: string;
    onVersionUpdated?: () => void;
}

const buildQueryPayload = (aiworkerId: string, configId?: string, configVersion?: number | string, configVersionId?: string) => {
    const filters: Record<string, unknown> = { aiworker_id: aiworkerId, config_name: 'agent_config' };
    if (configId) {
        filters.config_id = configId;
        if (configVersion !== undefined && configVersion !== null && configVersion !== '') filters.version = configVersion;
        if (configVersionId) filters.version_id = configVersionId;
    } else {
        filters.status = 'active';
        filters.is_active = true;
    }
    return { scope: 'tenant', pagination: { page: 1, per_page: 10 }, filters } as any;
};

/** A custom group holds `rename`/`merge` ops; anything else holds browser steps. */
const isCustomGroup = (groupKey: string, list: any[]) =>
    groupKey === 'custom' || list.some((entry) => typeof entry?.op === 'string');

/** Default new entry for a group (custom op vs. browser step). Ids are renumbered after insert. */
const makeEntry = (groupKey: string, list: any[]): any => {
    if (isCustomGroup(groupKey, list)) return { op: 'rename', from: '', to: '', match: 'exact' };
    return { id: `step-${list.length + 1}`, action: 'goto', value: '' };
};

/** Keep browser-step ids sequential (`step-1`…) after any list edit. */
const finalizeGroupList = (groupKey: string, list: any[]): any[] => {
    if (isCustomGroup(groupKey, list)) return list;
    return renumberWebAgentSteps(list as WebAgentStep[]);
};

const normalizeGroups = (src: Groups): Groups => {
    const out: Groups = {};
    for (const [key, list] of Object.entries(src)) {
        out[key] = finalizeGroupList(key, Array.isArray(list) ? deepClone(list) : []);
    }
    return out;
};

/** Keep only array-valued top-level keys (mirrors parseInstructionsGroups for edited JSON). */
const toGroups = (parsed: unknown): Groups => {
    const out: Groups = {};
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
            if (Array.isArray(value)) out[key] = value;
        }
    }
    return out;
};

const pretty = (value: unknown) => JSON.stringify(value, null, 2);

export function WebAgentInstructions({ agent, configId, configVersion, configVersionId, onVersionUpdated }: Readonly<WebAgentInstructionsProps>) {
    const { t } = useTranslation();
    const token = useTypedSelector((state) => state.auth.token);
    const { canUpdate } = useEntityResourcePermissions('agent', agent?.aiworker_id);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [showAssistant, setShowAssistant] = useState(true);
    const [currentSection, setCurrentSection] = useState('');
    const [view, setView] = useState<'form' | 'json'>('form');
    const [jsonText, setJsonText] = useState('');
    const [jsonError, setJsonError] = useState<string | null>(null);
    const [dataState, setDataState] = useState<Groups>({});
    const [initialData, setInitialData] = useState<Groups>({});
    const [updateConfiguration, { isLoading: isSaving }] = useUpdateAiWorkerConfigurationMutation();

    const queryPayload = useMemo(
        () => buildQueryPayload(agent?.aiworker_id ?? '', configId, configVersion, configVersionId),
        [agent?.aiworker_id, configId, configVersion, configVersionId],
    );

    const { data: configurationData, isLoading, isFetching, isError } = useGetAiWorkerConfigurationsQuery(queryPayload, {
        skip: !token || !agent?.aiworker_id,
        refetchOnMountOrArgChange: true,
    });

    const selectedConfiguration = configurationData?.configurations?.[0];
    const isInactiveConfiguration = selectedConfiguration?.status === 'inactive' || selectedConfiguration?.is_active === false;
    const isConfigurationEditable = canUpdate && !isInactiveConfiguration;
    const rootConfigurationId = selectedConfiguration?.configuration_id ?? '';

    const groups = useMemo(
        () => parseInstructionsGroups(toObject(selectedConfiguration?.config)),
        [selectedConfiguration?.config],
    );

    const availableInputs = useMemo(() => {
        const commandJson = extractCommandJsonFromAttrs(agent?.aiworkers_attrs);
        return parseFieldDefs(commandJson).map((fd) => fd.key);
    }, [agent?.aiworkers_attrs]);

    const startUrl = getWebAgentStartUrl(agent);

    useEffect(() => {
        // Show sequential ids in the editor; keep server snapshot as initial so zig-zag configs
        // stay dirty until Save writes step-1…N.
        setDataState(normalizeGroups(groups));
        setInitialData(deepClone(groups));
    }, [groups]);

    useEffect(() => {
        const media = globalThis.matchMedia('(max-width: 767px)');
        const handler = () => setIsCollapsed(media.matches);
        handler();
        media.addEventListener('change', handler);
        return () => media.removeEventListener('change', handler);
    }, []);

    const navItems = useMemo(
        () => Object.keys(dataState).map((groupKey) => ({
            key: groupKey,
            path: `/agents/instructions/${groupKey}`,
            label: formatKeyForDisplay(groupKey),
            icon: 'mdi:playlist-check',
        })),
        [dataState],
    );
    const navGroups = useMemo(() => [{ heading: null, items: navItems }], [navItems]);

    useEffect(() => {
        const keys = navItems.map((item) => item.key);
        if (keys.length === 0) {
            setCurrentSection('');
            return;
        }
        if (!keys.includes(currentSection)) setCurrentSection(keys[0]);
    }, [navItems, currentSection]);

    const updateGroup = (groupKey: string, updater: (list: any[]) => any[]) => {
        if (!isConfigurationEditable) return;
        setDataState((prev) => ({
            ...prev,
            [groupKey]: finalizeGroupList(groupKey, updater(prev[groupKey] ?? [])),
        }));
    };
    const patchEntry = (groupKey: string, index: number, partial: Record<string, any>) =>
        updateGroup(groupKey, (list) => list.map((item, i) => (i === index ? { ...item, ...partial } : item)));
    const patchTarget = (groupKey: string, index: number, partial: Partial<NonNullable<WebAgentStep['target']>>) =>
        updateGroup(groupKey, (list) => list.map((item, i) => (i === index ? { ...item, target: { ...item?.target, ...partial } } : item)));
    const addEntry = (groupKey: string) => updateGroup(groupKey, (list) => [...list, makeEntry(groupKey, list)]);
    const removeEntry = (groupKey: string, index: number) => updateGroup(groupKey, (list) => list.filter((_, i) => i !== index));
    const moveEntry = (groupKey: string, index: number, dir: -1 | 1) =>
        updateGroup(groupKey, (list) => {
            const j = index + dir;
            if (j < 0 || j >= list.length) return list;
            const next = [...list];
            [next[index], next[j]] = [next[j], next[index]];
            return next;
        });

    const applyGenerated = ({ target, mode, entries, range }: AssistantApplyPayload) => {
        if (!isConfigurationEditable) return;
        const existing = Array.isArray(dataState[target]) ? dataState[target] : [];
        let list: any[];
        const clamp = (n: number, max: number) => Math.max(0, Math.min(max, Math.trunc(Number.isFinite(n) ? n : 0)));
        if (mode === 'replace') {
            list = entries;
        } else if (mode === 'range') {
            const start = clamp(range?.start ?? 0, existing.length);
            const end = Math.max(start, clamp(range?.end ?? start, existing.length));
            list = [...existing.slice(0, start), ...entries, ...existing.slice(end)];
        } else if (mode === 'single') {
            const idx = clamp(range?.start ?? 0, Math.max(0, existing.length - 1));
            list = existing.length === 0 ? entries : [...existing.slice(0, idx), ...entries, ...existing.slice(idx + 1)];
        } else {
            list = [...existing, ...entries];
        }
        const nextList = finalizeGroupList(target, list);
        const next = { ...dataState, [target]: nextList };
        setDataState(next);
        setCurrentSection(target);
        setJsonError(null);
        if (view === 'json') setJsonText(pretty(next));
    };

    const changedGroupKeys = useMemo(() => {
        const keys = new Set([...Object.keys(dataState), ...Object.keys(initialData)]);
        return [...keys].filter((key) => JSON.stringify(dataState[key] ?? null) !== JSON.stringify(initialData[key] ?? null));
    }, [dataState, initialData]);
    const hasChanges = changedGroupKeys.length > 0;

    const showJson = () => {
        setJsonText(pretty(dataState));
        setJsonError(null);
        setView('json');
    };

    const onJsonChange = (value = '') => {
        if (!isConfigurationEditable) return;
        setJsonText(value);
        try {
            setDataState(normalizeGroups(toGroups(JSON.parse(value))));
            setJsonError(null);
        } catch (error: any) {
            setJsonError(error?.message ?? t('Invalid JSON'));
        }
    };

    const handleResetAll = () => {
        if (!isConfigurationEditable) return;
        const reset = deepClone(initialData);
        setDataState(reset);
        setJsonError(null);
        if (view === 'json') setJsonText(pretty(reset));
    };

    const handleSave = async () => {
        if (!isConfigurationEditable || !rootConfigurationId || !hasChanges || jsonError) return;
        try {
            const normalized = normalizeGroups(dataState);
            const payload: Record<string, unknown> = { scope: 'tenant' };
            const keysToSave = new Set(changedGroupKeys);
            for (const [key, list] of Object.entries(normalized)) {
                if (isCustomGroup(key, list)) continue;
                if (JSON.stringify(initialData[key] ?? []) !== JSON.stringify(list)) keysToSave.add(key);
            }
            for (const key of keysToSave) {
                payload[`config.instructions_data.${key}`] = normalized[key] ?? [];
            }
            await updateConfiguration({ configurationId: rootConfigurationId, payload }).unwrap();
            setDataState(normalized);
            setInitialData(deepClone(normalized));
            if (view === 'json') setJsonText(pretty(normalized));
            CtyShowToast(checkType.success, t('Instructions saved successfully.'));
            onVersionUpdated?.();
        } catch (error: any) {
            const errorMessage = error?.data?.message ?? error?.reason ?? error?.message ?? t('Failed to save instructions.');
            CtyShowToast(checkType.error, String(errorMessage));
        }
    };

    const renderRow = (entry: any, index: number, total: number, custom: boolean, list: any[]) => {
        const common = {
            index,
            total,
            isReadOnly: !isConfigurationEditable,
            onMove: (dir: -1 | 1) => moveEntry(currentSection, index, dir),
            onRemove: () => removeEntry(currentSection, index),
        };
        const key = `${currentSection}-${index}`;
        return custom ? (
            // eslint-disable-next-line react/no-array-index-key
            <CustomInstructionRow key={key} {...common} instruction={entry as WebAgentCustomInstruction} onChange={(partial) => patchEntry(currentSection, index, partial)} />
        ) : (
            // eslint-disable-next-line react/no-array-index-key
            <StepRow
              key={key}
              {...common}
              availableInputs={availableInputs}
              hasStartUrl={Boolean(startUrl)}
              priorStepIds={list.slice(0, index).map((s, i) => String(s?.id ?? `step-${i + 1}`))}
              storedVarNames={
                list.slice(0, index)
                  .filter((s: any) => s?.action === 'store' && s?.as)
                  .map((s: any) => String(s.as))
              }
              step={entry as WebAgentStep}
              repeatableSteps={list.flatMap((s: WebAgentStep, i: number) => (
                s?.action === 'forEach' ? [{ id: String(s.id ?? `step-${i + 1}`), label: `${s.id ?? `step-${i + 1}`} ${s.value ?? ''}`.trim() }] : []
              ))}
              onStep={(partial) => patchEntry(currentSection, index, partial)}
              onTarget={(partial) => patchTarget(currentSection, index, partial)}
            />
        );
    };

    const renderFormBody = () => {
        if (navItems.length === 0) return <p className="text-sm text-default-500">{t('No instructions found for this agent.')}</p>;
        const list = dataState[currentSection] ?? [];
        const custom = isCustomGroup(currentSection, list);
        return (
            <div className="flex flex-col gap-3">
                {list.map((entry, index) => renderRow(entry, index, list.length, custom, list))}
                {isConfigurationEditable && (
                    <CtyButton size="sm" variant="bordered" className="self-start" startContent={<Icon icon="mdi:plus" />} onPress={() => addEntry(currentSection)}>
                        {custom ? t('Add instruction') : t('Add step')}
                    </CtyButton>
                )}
            </div>
        );
    };

    const renderForm = () => (
        <div className="flex flex-1 min-h-0 overflow-hidden">
            <NavSidebar
                navGroups={navGroups}
                currentSection={currentSection}
                isCollapsed={isCollapsed}
                onToggleCollapse={() => setIsCollapsed((value) => !value)}
                headerTitle={t('Instructions')}
                expandMenuLabel={t('Expand menu')}
                collapseMenuLabel={t('Collapse menu')}
                onInternalNavigate={(_, item) => setCurrentSection(item.key)}
                navClassName="pt-0"
            />
            <main className="flex-1 min-w-0 overflow-y-auto p-4 bg-default-50">
                <Card className="border border-divider shadow-none">
                    <CardBody className="gap-3">
                        <h3 className="text-lg font-semibold">{currentSection ? formatKeyForDisplay(currentSection) : t('Instructions')}</h3>
                        {renderFormBody()}
                    </CardBody>
                </Card>
            </main>
        </div>
    );

    const renderJson = () => (
        <div className="flex-1 min-h-0 flex flex-col p-4 bg-default-50">
            {jsonError && <p className="text-sm text-danger mb-2">{jsonError}</p>}
            <div className="flex-1 min-h-0 overflow-hidden">
                <JsonMapperMonacoEditor
                    ariaLabel="instructions_json"
                    original={pretty(initialData)}
                    value={jsonText}
                    onChange={onJsonChange}
                    isReadOnly={!isConfigurationEditable}
                />
            </div>
        </div>
    );

    const renderContent = () => {
        if (!agent?.aiworker_id) return <p className="text-sm text-default-500 p-4">{t('AI Worker ID is missing for this agent.')}</p>;
        if (isLoading || isFetching) return <div className="p-4"><CtySpinner size="lg" /></div>;
        if (isError) return <p className="text-sm text-danger p-4">{t('Failed to load instructions.')}</p>;
        return view === 'json' ? renderJson() : renderForm();
    };

    const subTabs = [
        { key: 'form', title: t('Form View'), id: 'instructions-form-tab' },
        { key: 'json', title: t('JSON View'), id: 'instructions-json-tab' },
    ];

    return (
        <div className="flex h-full min-h-0 overflow-hidden border border-divider bg-content1">
            <div className="flex flex-1 min-w-0 flex-col overflow-hidden">
                <div className="shrink-0 flex items-center justify-between gap-2 border-b border-divider px-3 py-2">
                    <CustomTabs tabs={subTabs} selectedKey={view} onTabChange={(key) => (key === 'json' ? showJson() : setView('form'))} />
                    <div className="flex items-center gap-2">
                        <CtyButton size="sm" variant={showAssistant ? 'solid' : 'bordered'} color={showAssistant ? 'primary' : 'default'} startContent={<Icon icon="mdi:robot-happy-outline" />} onPress={() => setShowAssistant((value) => !value)}>
                            {t('AI Assistant')}
                        </CtyButton>
                        <CtyButton size="sm" variant="bordered" onPress={handleResetAll} isDisabled={!isConfigurationEditable || !hasChanges}>
                            {t('Reset All')}
                        </CtyButton>
                        <CtyButton size="sm" color="primary" onPress={handleSave} isDisabled={!isConfigurationEditable || !rootConfigurationId || !hasChanges || Boolean(jsonError)} isLoading={isSaving}>
                            {t('Save')}
                        </CtyButton>
                    </div>
                </div>
                {renderContent()}
            </div>
            {/* Kept mounted while this tab is open so collapsing the panel does not discard the chat;
                leaving the Instructions tab unmounts this component and resets it. */}
            <WebAgentInstructionsAssistant agent={agent} isReadOnly={!isConfigurationEditable} startUrl={startUrl} existingSteps={dataState.steps ?? []} existingCustom={dataState.custom ?? []} availableInputs={availableInputs} isHidden={!showAssistant} onApply={applyGenerated} />
        </div>
    );
}

export default WebAgentInstructions;
